(function () {
  const DATA_ID = "ss-fit-finder-data";
  const STATE_KEY = "ss_fit_finder_state_v1";
  const STATE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  const CONTACT_EMAIL = "info@seymoursolar.com";
  const FIT_PROPERTY_NAMES = new Set([
    "properties[_Fit Finder Session]",
    "properties[_Fit Finder Case]",
    "properties[_Fit Finder Input]",
    "properties[_Equipment]",
    "properties[_Measured OD]",
    "properties[_Recommended Size]",
    "properties[_Model Recommendation]",
    "properties[_Fit Finder Match]",
    "properties[_Compatibility Record]",
  ]);

  function parseData() {
    const node = document.getElementById(DATA_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || "{}");
    } catch (error) {
      return null;
    }
  }

  function newSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return `ff_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
  }

  function loadState() {
    let saved = null;
    try {
      saved = JSON.parse(window.localStorage.getItem(STATE_KEY) || "null");
    } catch (error) {}
    if (!saved || !saved.fit_session_id || Date.now() - Number(saved.updated_at || 0) > STATE_TTL_MS) {
      saved = { fit_session_id: newSessionId(), created_at: Date.now() };
    }
    saved.updated_at = Date.now();
    return saved;
  }

  let state = loadState();

  window.addEventListener("ss-fit-finder-case-started", (event) => {
    if (event.detail && typeof event.detail === "object") state = event.detail;
  });

  function beginCase(seed) {
    if (window.SeymourFitFinderBackup && typeof window.SeymourFitFinderBackup.startCase === "function") {
      state = window.SeymourFitFinderBackup.startCase(seed || {});
      return;
    }
    state = {
      fit_session_id: state.fit_session_id || newSessionId(),
      created_at: state.created_at || Date.now(),
      fit_case_id: `ffcase_${newSessionId()}`,
      case_started_at: Date.now(),
      ...(seed || {}),
    };
    saveState({});
  }

  function saveState(changes) {
    state = { ...state, ...changes, updated_at: Date.now(), case_updated_at: Date.now() };
    try {
      window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (error) {}
    syncProductForms();
  }

  function track(eventName, details) {
    const evt = {
      event: eventName,
      fit_session_id: state.fit_session_id,
      fit_case_id: state.fit_case_id,
      ...details,
    };
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(evt);
    } catch (error) {}
    try {
      if (window.SeymourFitFinderBackup) {
        window.SeymourFitFinderBackup.send(eventName, evt);
      }
    } catch (error) {}
  }

  function normalize(value) {
    return (value || "")
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .replace(/\s+/g, " ");
  }

  function recordTerms(record) {
    const aliases = Array.isArray(record.aliases) ? record.aliases : [];
    return [
      `${record.manufacturer} ${record.model}`,
      record.model,
      ...aliases,
      ...aliases.map((alias) => `${record.manufacturer} ${alias}`),
    ].map(normalize);
  }

  function exactRecord(records, query) {
    const normalized = normalize(query);
    if (!normalized) return null;
    return records.find((record) => recordTerms(record).includes(normalized)) || null;
  }

  function suggestions(records, query) {
    const normalized = normalize(query);
    if (normalized.length < 2) return [];
    return records.filter((record) => {
      return recordTerms(record).some((term) => term.includes(normalized) || normalized.includes(term));
    }).slice(0, 5);
  }

  function allProducts(data) {
    return [data.canonical, ...(Array.isArray(data.equivalents) ? data.equivalents : [])].filter(Boolean);
  }

  function variantIndex(data) {
    const map = new Map();
    allProducts(data).forEach((product) => {
      (Array.isArray(product.variants) ? product.variants : []).forEach((variant) => {
        map.set(String(variant.id), { product, variant });
      });
    });
    return map;
  }

  function setText(node, value) {
    node.textContent = value;
    return node;
  }

  function makeElement(tag, attributes) {
    const node = document.createElement(tag);
    Object.entries(attributes || {}).forEach(([name, value]) => {
      if (name === "className") node.className = value;
      else node.setAttribute(name, value);
    });
    return node;
  }

  function equipmentDescription() {
    return state.original_model_query || [state.manufacturer_input, state.model_input].filter(Boolean).join(" ");
  }

  function availabilityMailto(details) {
    const size = details.size || state.recommended_filter_size || "the recommended size";
    const equipment = details.equipment || equipmentDescription();
    const measurement = details.measurement || (state.measurement_value ? `${state.measurement_value} ${state.measurement_unit}` : "");
    const subject = `Availability request - ${size} filter`;
    const body = [
      `I'm interested in the ${size} filter recommended by the Seymour Solar Fit Finder.`,
      equipment ? `Equipment: ${equipment}` : "",
      measurement ? `Measurement provided: ${measurement}` : "",
      details.sku ? `Current product SKU: ${details.sku}` : "",
      `Fit Finder case: ${state.fit_case_id || "not available"}`,
      `Fit Finder session: ${state.fit_session_id}`,
      "Please let me know about availability or notify me when this size is available.",
    ].filter(Boolean).join("\n\n");
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function availabilityEmailLink(details) {
    const link = makeElement("a", {
      href: availabilityMailto(details),
      className: "ss-ff-availability-email",
    });
    setText(link, "Email About This Size");
    link.addEventListener("click", () => {
      link.href = availabilityMailto(details);
      track("fit_finder_availability_email_clicked", {
        source: details.source,
        recommended_size: details.size,
        sku: details.sku || "",
        equipment: details.equipment || equipmentDescription(),
      });
    });
    return link;
  }

  function conflictMailto(details) {
    const subject = "Fit Finder recommendation conflict";
    const body = [
      "The Seymour Solar Fit Finder found conflicting recommendations.",
      details.equipment ? `Equipment: ${details.equipment}` : "",
      details.modelSize ? `Model recommendation: ${details.modelSize}` : "",
      details.measurementSize ? `Measurement recommendation: ${details.measurementSize}` : "",
      details.measurement ? `Measurement provided: ${details.measurement}` : "",
      `Fit Finder case: ${state.fit_case_id || "not available"}`,
      `Fit Finder session: ${state.fit_session_id}`,
      "Please help me confirm the correct filter size before I order.",
    ].filter(Boolean).join("\n\n");
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function clearProductFormLinkage() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form.querySelectorAll('input[name^="properties["]').forEach((input) => {
        if (FIT_PROPERTY_NAMES.has(input.name)) input.remove();
      });
    });
  }

  function showConflict(panel, details) {
    panel.querySelectorAll(".ss-ff-model-option").forEach((link) => link.remove());
    const container = panel.querySelector("[data-ff-results]");
    if (container) container.innerHTML = "";
    const results = panel.querySelector('[data-ff-step="results"]');
    if (!results) return;
    results.querySelectorAll(".ss-ff-conflict").forEach((node) => node.remove());
    const warning = makeElement("div", { className: "ss-ff-conflict", role: "alert" });
    const message = makeElement("div", {});
    setText(message, `Your measurement points to a different size than the ${details.modelSize} model recommendation. No product recommendation is being shown. Confirm where you measured or contact us before ordering.`);
    const contact = makeElement("a", {
      href: conflictMailto(details),
      className: "ss-ff-contact-link",
    });
    setText(contact, "Contact Seymour Solar");
    contact.addEventListener("click", () => {
      contact.href = conflictMailto(details);
      track("fit_finder_availability_email_clicked", {
        source: "conflict",
        recommended_size: details.measurementSize || "",
        equipment: details.equipment || equipmentDescription(),
      });
    });
    warning.append(message, contact);
    results.prepend(warning);
  }

  function renderModelResult(resultNode, record, data) {
    resultNode.innerHTML = "";
    resultNode.style.display = "block";

    const index = variantIndex(data);
    const resolved = (record.variant_ids || []).map((id) => index.get(String(id))).filter(Boolean);
    const sizeSku = (resolved.find((item) => item.variant.sku) || { variant: {} }).variant.sku || "";

    const heading = makeElement("div", { className: "ss-ff-model-result__heading" });
    setText(heading, `${record.manufacturer} ${record.model}`);
    const size = makeElement("div", { className: "ss-ff-model-result__size" });
    setText(size, `Required filter size: ${record.recommended_size}`);
    const basis = makeElement("div", { className: "ss-ff-model-result__basis" });
    setText(basis, record.evidence_label);
    const note = makeElement("div", { className: "ss-ff-model-result__note" });
    setText(note, record.customer_note || "For the standard, unmodified model.");
    resultNode.append(heading, size, basis, note);

    const available = resolved.filter((item) => item.variant.available === true);
    const options = makeElement("div", { className: "ss-ff-model-options" });

    if (available.length) {
      available.forEach((item) => {
        const link = makeElement("a", { className: "ss-ff-model-option" });
        link.href = `${item.product.url}?variant=${encodeURIComponent(item.variant.id)}`;
        const title = makeElement("span", { className: "ss-ff-model-option__title" });
        setText(title, item.product.title);
        const action = makeElement("span", { className: "ss-ff-model-option__action" });
        setText(action, "View available option");
        link.append(title, action);
        link.addEventListener("click", () => {
          saveState({
            product_click: true,
            selected_variant_id: String(item.variant.id),
          });
          track("fit_finder_model_result_clicked", {
            record_id: record.record_id,
            recommended_size: record.recommended_size,
            product_handle: item.product.handle,
            variant_id: String(item.variant.id),
          });
        });
        options.appendChild(link);
      });
    } else {
      const unavailable = makeElement("div", { className: "ss-ff-model-unavailable" });
      setText(unavailable, "This size is currently unavailable online. Need this filter? Email us to ask about availability or request a restock notification.");
      const actions = makeElement("div", { className: "ss-ff-model-unavailable__actions" });
      const contact = availabilityEmailLink({
        source: "model",
        size: record.recommended_size,
        sku: sizeSku,
        equipment: `${record.manufacturer} ${record.model}`,
      });
      const shop = makeElement("a", { href: "/collections/telescope-solar-filters" });
      setText(shop, "Continue shopping");
      actions.append(contact, shop);
      options.append(unavailable, actions);
    }

    resultNode.appendChild(options);
    saveState({
      equipment_type: record.equipment_type,
      manufacturer_input: record.manufacturer,
      model_input: record.model,
      normalized_manufacturer: normalize(record.manufacturer),
      normalized_model: normalize(record.model),
      recommendation_record_id: record.record_id,
      recommended_filter_size: record.recommended_size,
      recommended_variant_ids: record.variant_ids,
      model_recommended_filter_size: record.recommended_size,
      model_variant_ids: record.variant_ids,
      match_result: "matched",
      outcome: available.length ? "model_match" : "unavailable",
      available_option_count: available.length,
      input_method: state.measurement_mm ? "both" : "make_model",
    });
    track("fit_finder_model_match_found", {
      record_id: record.record_id,
      manufacturer: record.manufacturer,
      model: record.model,
      recommended_size: record.recommended_size,
      available_options: available.length,
      model_recommended_size: record.recommended_size,
      outcome: available.length ? "model_match" : "unavailable",
    });
  }

  function renderSuggestions(listNode, records, query, input, resultNode, data) {
    listNode.innerHTML = "";
    const found = suggestions(records, query);
    if (!found.length) {
      listNode.style.display = "none";
      input.setAttribute("aria-expanded", "false");
      return;
    }
    found.forEach((record, index) => {
      const button = makeElement("button", {
        type: "button",
        className: "ss-ff-model-suggestion",
        role: "option",
        id: `ss-ff-model-option-${index}`,
      });
      setText(button, `${record.manufacturer} ${record.model}`);
      button.addEventListener("click", () => {
        input.value = `${record.manufacturer} ${record.model}`;
        listNode.style.display = "none";
        input.setAttribute("aria-expanded", "false");
        renderModelResult(resultNode, record, data);
      });
      button.addEventListener("keydown", (event) => {
        const options = Array.from(listNode.querySelectorAll('[role="option"]'));
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const direction = event.key === "ArrowDown" ? 1 : -1;
          options[(index + direction + options.length) % options.length].focus();
        } else if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          listNode.style.display = "none";
          input.setAttribute("aria-expanded", "false");
          input.focus();
        }
      });
      listNode.appendChild(button);
    });
    listNode.style.display = "grid";
    input.setAttribute("aria-expanded", "true");
  }

  function addModelSearch(panel, data) {
    if (panel.querySelector("[data-ff-model-search]")) return;
    const records = Array.isArray(data.compatibility_records) ? data.compatibility_records : [];
    const content = panel.children[1];
    if (!content || !records.length) return;

    const section = makeElement("section", { "data-ff-model-search": "", className: "ss-ff-model-search" });
    const title = makeElement("label", { className: "ss-ff-model-search__title", for: "ss-ff-model-input" });
    setText(title, "Search by telescope make or model");
    const sub = makeElement("div", { className: "ss-ff-model-search__sub" });
    setText(sub, "Optional. If your model is not listed, use the measurement finder below.");
    const row = makeElement("div", { className: "ss-ff-model-search__row" });
    const input = makeElement("input", {
      type: "search",
      id: "ss-ff-model-input",
      autocomplete: "off",
      placeholder: "Example: Sky-Watcher 300P",
      role: "combobox",
      "aria-autocomplete": "list",
      "aria-controls": "ss-ff-model-suggestions",
      "aria-expanded": "false",
    });
    const button = makeElement("button", { type: "button" });
    setText(button, "Find my filter");
    const list = makeElement("div", { id: "ss-ff-model-suggestions", className: "ss-ff-model-suggestions", role: "listbox", "aria-label": "Matching telescope models" });
    const result = makeElement("div", { className: "ss-ff-model-result", role: "status", "aria-live": "polite" });
    row.append(input, button);
    section.append(title, sub, row, list, result);

    const divider = makeElement("div", { className: "ss-ff-divider" });
    setText(divider, "Or use a measurement");
    content.prepend(divider);
    content.prepend(section);

    input.value = state.original_model_query || "";
    input.addEventListener("input", () => renderSuggestions(list, records, input.value, input, result, data));
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        button.click();
      } else if (event.key === "ArrowDown" && input.getAttribute("aria-expanded") === "true") {
        const first = list.querySelector('[role="option"]');
        if (first) {
          event.preventDefault();
          first.focus();
        }
      } else if (event.key === "Escape" && input.getAttribute("aria-expanded") === "true") {
        event.preventDefault();
        event.stopPropagation();
        list.style.display = "none";
        input.setAttribute("aria-expanded", "false");
      }
    });
    button.addEventListener("click", () => {
      const raw = input.value.trim();
      if (!raw) return;
      if (!state.fit_case_id || normalize(raw) !== normalize(state.original_model_query || "")) {
        beginCase({ original_model_query: raw, optional_scope_provided: true });
      }
      saveState({ original_model_query: raw, optional_scope_provided: true });
      track("fit_finder_model_searched", { model_query: raw });
      const match = exactRecord(records, raw);
      if (match) {
        list.style.display = "none";
        input.setAttribute("aria-expanded", "false");
        renderModelResult(result, match, data);
        return;
      }
      const found = suggestions(records, raw);
      if (found.length) {
        renderSuggestions(list, records, raw, input, result, data);
        result.style.display = "block";
        setText(result, "Choose the exact model from the suggestions, or use a measurement below.");
      } else {
        list.style.display = "none";
        input.setAttribute("aria-expanded", "false");
        result.style.display = "block";
        setText(result, "We don’t have a verified slip-on fit for that equipment yet. Enter the outside diameter of the front rim below and we’ll match it to the correct filter size.");
        saveState({ match_result: "measurement_required", outcome: "unknown_model" });
        track("fit_finder_model_no_match", { model_query: raw, outcome: "unknown_model" });
      }
    });
  }

  function currentMeasurement(data, value, unit) {
    const number = Number((value || "").trim().replace(",", "."));
    if (!Number.isFinite(number)) return null;
    const mm = unit === "in" ? number * 25.4 : number;
    const variants = (data.size_variants || [])
      .filter((variant) => Number.isFinite(Number(variant.min_mm)) && Number.isFinite(Number(variant.max_mm)))
      .slice()
      .sort((a, b) => Number(a.min_mm) - Number(b.min_mm));
    const smallest = variants[0];
    const largest = variants[variants.length - 1];
    let match = null;
    if (smallest && largest && Number(smallest.min_mm) <= mm && mm <= Number(largest.max_mm)) {
      match = variants.find((variant) => {
        return Number(variant.min_mm) <= mm && mm <= Number(variant.max_mm);
      }) || variants.find((variant) => mm < Number(variant.min_mm)) || null;
    }
    return { number, unit, mm, match };
  }

  function availableForSize(data, match) {
    if (!match) return [];
    const title = normalize(match.title);
    const sizeToken = (title.match(/^\d+(?: \d+)?(?: \d+)?/) || [""])[0];
    const options = [];
    allProducts(data).forEach((product) => {
      (product.variants || []).forEach((variant) => {
        if (variant.available === true && normalize(variant.title).startsWith(sizeToken)) {
          options.push(String(variant.id));
        }
      });
    });
    return options;
  }

  function addOptionalEquipmentPrompt(panel, data) {
    if (state.original_model_query || state.model_input || state.optional_scope_prompt_shown) return;
    const results = panel.querySelector('[data-ff-step="results"]');
    if (!results || results.style.display === "none") return;
    saveState({ optional_scope_prompt_shown: true });
    const prompt = makeElement("div", { className: "ss-ff-optional-equipment" });
    const label = makeElement("label", {});
    setText(label, "Optional: What telescope or scope is this for?");
    const input = makeElement("input", { type: "text", placeholder: "Make and model" });
    const save = makeElement("button", { type: "button" });
    setText(save, "Save");
    save.addEventListener("click", () => {
      const raw = input.value.trim();
      if (!raw) return;
      const records = Array.isArray(data.compatibility_records) ? data.compatibility_records : [];
      const record = exactRecord(records, raw);
      const measurementIds = Array.isArray(state.recommended_variant_ids)
        ? state.recommended_variant_ids.map(String)
        : [];
      const modelIds = record && Array.isArray(record.variant_ids)
        ? record.variant_ids.map(String)
        : [];
      const conflict = Boolean(record && measurementIds.length && modelIds.length)
        && !measurementIds.some((id) => modelIds.includes(id));
      saveState({
        original_model_query: raw,
        optional_scope_provided: true,
        input_method: "both",
        equipment_type: record ? record.equipment_type : state.equipment_type,
        manufacturer_input: record ? record.manufacturer : state.manufacturer_input,
        model_input: record ? record.model : state.model_input,
        normalized_manufacturer: record ? normalize(record.manufacturer) : state.normalized_manufacturer,
        normalized_model: record ? normalize(record.model) : normalize(raw),
        recommendation_record_id: record ? record.record_id : state.recommendation_record_id,
        model_recommended_filter_size: record ? record.recommended_size : state.model_recommended_filter_size,
        model_variant_ids: record ? record.variant_ids : state.model_variant_ids,
        match_result: conflict ? "conflict" : state.match_result,
        outcome: conflict ? "conflict" : (record ? state.outcome : "unknown_model"),
        recommended_variant_ids: conflict ? [] : state.recommended_variant_ids,
        allowed_product_handles: conflict ? [] : state.allowed_product_handles,
        selected_variant_id: conflict ? "" : state.selected_variant_id,
      });
      track("fit_finder_optional_equipment_provided", { model_query: raw });
      if (record) {
        track("fit_finder_model_match_found", {
          record_id: record.record_id,
          manufacturer: record.manufacturer,
          model: record.model,
          recommended_size: record.recommended_size,
          available_options: measurementIds.length,
          model_recommended_size: record.recommended_size,
          outcome: measurementIds.length ? "model_match" : "unavailable",
        });
      }
      prompt.innerHTML = "";
      if (conflict) {
        clearProductFormLinkage();
        showConflict(panel, {
          equipment: `${record.manufacturer} ${record.model}`,
          modelSize: record.recommended_size,
          measurementSize: state.recommended_filter_size,
          measurement: state.measurement_value ? `${state.measurement_value} ${state.measurement_unit}` : "",
        });
        track("fit_finder_recommendation_conflict", {
          recommendation_record_id: record.record_id,
          model_recommended_size: record.recommended_size,
          measurement_recommended_size: state.recommended_filter_size,
          outcome: "conflict",
        });
        setText(prompt, "This equipment does not agree with the measurement result. Review the conflict above before ordering.");
      } else if (record) {
        setText(prompt, "Thank you. The approved model recommendation agrees with your measurement.");
      } else {
        setText(prompt, "Thank you. We do not have an approved model match yet, so your measurement result is unchanged.");
        track("fit_finder_model_no_match", { model_query: raw, outcome: "unknown_model" });
      }
    });
    prompt.append(label, input, save);
    results.appendChild(prompt);
    track("fit_finder_optional_equipment_prompt_shown", {});
  }

  function guardMeasurementResults(panel, data, measurement) {
    if (!measurement) return;
    if (!measurement.match) {
      saveState({
        measurement_value: measurement.number,
        measurement_unit: measurement.unit,
        measurement_mm: Math.round(measurement.mm * 100) / 100,
        recommended_filter_size: "",
        recommended_variant_ids: [],
        match_result: "no_match",
        outcome: "no_size_match",
        input_method: state.original_model_query ? "both" : "measurement",
      });
      return;
    }
    const ids = availableForSize(data, measurement.match);
    const allSizeIds = [];
    const sizeTokenMatch = normalize(measurement.match.title).match(/^\d+(?: \d+)?(?: \d+)?/);
    allProducts(data).forEach((product) => {
      (product.variants || []).forEach((variant) => {
        if (sizeTokenMatch && normalize(variant.title).startsWith(sizeTokenMatch[0])) allSizeIds.push(String(variant.id));
      });
    });
    const modelIds = Array.isArray(state.model_variant_ids) ? state.model_variant_ids.map(String) : [];
    const conflict = modelIds.length > 0 && !allSizeIds.some((id) => modelIds.includes(String(id)));
    const outcome = conflict ? "conflict" : (ids.length ? "measurement_match" : "unavailable");
    saveState({
      measurement_value: measurement.number,
      measurement_unit: measurement.unit,
      measurement_mm: Math.round(measurement.mm * 100) / 100,
      recommended_filter_size: measurement.match.title,
      measurement_recommended_filter_size: measurement.match.title,
      recommended_variant_ids: conflict ? [] : ids,
      match_result: conflict ? "conflict" : "matched",
      outcome,
      available_option_count: ids.length,
      input_method: state.original_model_query ? "both" : "measurement",
      allowed_product_handles: conflict ? [] : allProducts(data).map((product) => product.handle),
      selected_variant_id: conflict ? "" : state.selected_variant_id,
    });
    const container = panel.querySelector("[data-ff-results]");
    const resultLinks = panel.querySelectorAll('[data-ff-results] [data-ff-product-option]');
    resultLinks.forEach((link) => {
      const id = new URL(link.href, window.location.origin).searchParams.get("variant");
      const indexed = variantIndex(data).get(String(id));
      if (!id || !indexed || indexed.variant.available === false) {
        const card = link.parentElement && link.parentElement.parentElement;
        if (card) card.remove();
      }
    });
    if (!conflict && container && !container.children.length) {
      const sizeSku = measurement.match.sku || "";
      const sizeResult = makeElement("div", { className: "ss-ff-model-result__size" });
      setText(sizeResult, `Your measurement matches this required filter size: ${measurement.match.title}`);
      const message = makeElement("div", { className: "ss-ff-model-unavailable" });
      setText(message, "This size is currently unavailable online. Need this filter? Email us to ask about availability or request a restock notification.");
      const actions = makeElement("div", { className: "ss-ff-model-unavailable__actions" });
      const contact = availabilityEmailLink({
        source: "measurement",
        size: measurement.match.title,
        sku: sizeSku,
        measurement: `${measurement.number} ${measurement.unit}`,
      });
      const shop = makeElement("a", { href: "/collections/telescope-solar-filters" });
      setText(shop, "Continue shopping");
      actions.append(contact, shop);
      container.append(sizeResult, message, actions);
    }
    if (conflict) {
      clearProductFormLinkage();
      showConflict(panel, {
        equipment: equipmentDescription(),
        modelSize: state.model_recommended_filter_size,
        measurementSize: measurement.match.title,
        measurement: `${measurement.number} ${measurement.unit}`,
      });
      track("fit_finder_recommendation_conflict", {
        recommendation_record_id: state.recommendation_record_id || "",
        model_recommended_size: state.model_recommended_filter_size || "",
        measurement_recommended_size: measurement.match.title,
        outcome: "conflict",
      });
    } else {
      track("fit_finder_measurement_outcome", {
        outcome,
        recommended_size: measurement.match.title,
        measurement_recommended_size: measurement.match.title,
        available_options: ids.length,
        od_input_value: measurement.number,
        od_input_unit: measurement.unit,
        od_normalized_mm: Math.round(measurement.mm * 100) / 100,
      });
    }
    addOptionalEquipmentPrompt(panel, data);
  }

  function hiddenInput(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!value) {
      if (input) input.remove();
      return;
    }
    if (!input) {
      input = makeElement("input", { type: "hidden", name });
      form.appendChild(input);
    }
    input.value = String(value).slice(0, 255);
  }

  function syncProductForms() {
    if (!state || !state.fit_session_id) return;
    if (!window.location.pathname.includes("/products/")) return;
    const handle = window.location.pathname.split("/products/")[1].split("/")[0];
    if (!Array.isArray(state.allowed_product_handles) || !state.allowed_product_handles.includes(handle)) return;
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      hiddenInput(form, "properties[_Fit Finder Session]", state.fit_session_id);
      hiddenInput(form, "properties[_Fit Finder Case]", state.fit_case_id);
      hiddenInput(form, "properties[_Fit Finder Input]", state.input_method);
      hiddenInput(form, "properties[_Equipment]", state.original_model_query || [state.manufacturer_input, state.model_input].filter(Boolean).join(" "));
      hiddenInput(form, "properties[_Measured OD]", state.measurement_value ? `${state.measurement_value} ${state.measurement_unit}` : "");
      hiddenInput(form, "properties[_Recommended Size]", state.recommended_filter_size);
      hiddenInput(form, "properties[_Model Recommendation]", state.model_recommended_filter_size);
      hiddenInput(form, "properties[_Fit Finder Match]", state.match_result);
      hiddenInput(form, "properties[_Compatibility Record]", state.recommendation_record_id);
      if (!form.dataset.fitFinderTracked) {
        form.dataset.fitFinderTracked = "true";
        form.addEventListener("submit", () => {
          track("fit_finder_cart_linked", {
            input_method: state.input_method || "",
            recommendation_record_id: state.recommendation_record_id || "",
            recommended_size: state.recommended_filter_size || "",
          });
        });
      }
    });
  }

  function addProminentSizingGuideEntry() {
    if (window.location.pathname !== "/pages/sizing-guide") return;
    if (document.querySelector("[data-ff-prominent-entry]")) return;

    const guide = document.querySelector(".obx-learn-sizing-fit");
    const header = guide && guide.querySelector("header");
    if (!guide || !header) return;

    const entry = makeElement("section", {
      className: "ss-ff-entry-card",
      "data-ff-prominent-entry": "",
      "aria-labelledby": "ss-ff-entry-title",
    });
    const copy = makeElement("div", { className: "ss-ff-entry-card__copy" });
    const title = makeElement("h2", { id: "ss-ff-entry-title" });
    setText(title, "Find the right slip-on filter");
    const description = makeElement("p", {});
    setText(description, "Search by telescope make and model, or enter one outside-diameter measurement.");
    const button = makeElement("button", {
      type: "button",
      className: "ss-ff-entry-card__button",
      "data-fit-finder-open": "",
      "data-fit-finder-entry": "sizing_guide_top",
    });
    setText(button, "Find My Slip-On Filter Size");
    button.addEventListener("click", () => {
      track("fit_finder_prominent_entry_clicked", { source: "sizing_guide_top" });
    });

    copy.append(title, description);
    entry.append(copy, button);
    header.insertAdjacentElement("afterend", entry);
  }

  function addContextualEntries(data) {
    const path = window.location.pathname;

    if (path === "/collections/telescope-solar-filters" && !document.querySelector("[data-ff-collection-entry]")) {
      const productList = document.querySelector(".shopify-section--main-collection .collection__main .product-list");
      if (productList) {
        const entry = makeElement("section", {
          className: "ss-ff-context-card ss-ff-context-card--collection",
          "data-ff-collection-entry": "",
          "aria-labelledby": "ss-ff-collection-entry-title",
        });
        const copy = makeElement("div", { className: "ss-ff-context-card__copy" });
        const title = makeElement("h2", { id: "ss-ff-collection-entry-title" });
        setText(title, "Need help choosing a size?");
        const description = makeElement("p", {});
        setText(description, "Search by telescope make and model, or enter one outside-diameter measurement.");
        const button = makeElement("button", {
          type: "button",
          className: "ss-ff-context-card__button",
          "data-fit-finder-open": "",
          "data-fit-finder-entry": "telescope_collection",
        });
        setText(button, "Use the Fit Finder");
        button.addEventListener("click", () => track("fit_finder_context_entry_clicked", { source: "telescope_collection" }));
        copy.append(title, description);
        entry.append(copy, button);
        productList.insertAdjacentElement("beforebegin", entry);
      }
    }

    if (path.indexOf("/products/") === 0 && !document.querySelector("[data-ff-product-entry]")) {
      const handle = path.split("/products/")[1].split("/")[0];
      const supported = allProducts(data).some((product) => product.handle === handle);
      const picker = document.querySelector('.product-info__block-item[data-block-type="variant-picker"]');
      if (supported && picker) {
        const entry = makeElement("div", { className: "ss-ff-product-entry", "data-ff-product-entry": "" });
        const text = makeElement("span", {});
        setText(text, "Not sure which slip-on size fits?");
        const button = makeElement("button", {
          type: "button",
          className: "ss-ff-product-entry__button",
          "data-fit-finder-open": "",
          "data-fit-finder-entry": "product_size_selector",
        });
        setText(button, "Use the Fit Finder");
        button.addEventListener("click", () => track("fit_finder_context_entry_clicked", { source: "product_size_selector", product_handle: handle }));
        entry.append(text, button);
        picker.insertAdjacentElement("afterend", entry);
      }
    }
  }

  function installStyles() {
    if (document.getElementById("ss-fit-finder-model-styles")) return;
    const style = document.createElement("style");
    style.id = "ss-fit-finder-model-styles";
    style.textContent = `
      .ss-ff-model-search{padding:14px;border:1px solid rgba(0,0,0,.12);border-radius:12px;background:#f7f5f1;margin-bottom:14px}
      .ss-ff-model-search__title{font-weight:700;font-size:16px}
      .ss-ff-model-search__sub{font-size:12px;opacity:.78;margin:4px 0 10px}
      .ss-ff-model-search__row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px}
      .ss-ff-model-search input,.ss-ff-optional-equipment input{width:100%;padding:11px;border:1px solid rgba(0,0,0,.2);border-radius:10px;background:#fff;color:#111}
      .ss-ff-model-search button,.ss-ff-optional-equipment button{padding:11px 13px;border:0;border-radius:10px;background:#111;color:#fff;cursor:pointer}
      .ss-ff-model-suggestions{display:none;gap:6px;margin-top:8px}
      .ss-ff-model-suggestion{width:100%;text-align:left!important;background:#fff!important;color:#111!important;border:1px solid rgba(0,0,0,.16)!important}
      .ss-ff-model-result{display:none;margin-top:10px;padding:12px;border:1px solid rgba(0,0,0,.12);border-radius:10px;background:#fff}
      .ss-ff-model-result__heading{font-weight:700}.ss-ff-model-result__size{font-weight:700;margin-top:6px}.ss-ff-model-result__basis,.ss-ff-model-result__note{font-size:12px;opacity:.78;margin-top:4px}
      .ss-ff-model-options{display:grid;gap:8px;margin-top:10px}.ss-ff-model-option{display:flex;justify-content:space-between;gap:12px;align-items:center;padding:10px;border:1px solid rgba(0,0,0,.12);border-radius:10px;color:#111;text-decoration:none}.ss-ff-model-option__title{font-size:12px}.ss-ff-model-option__action{font-weight:700;white-space:nowrap}
      .ss-ff-model-unavailable{font-size:13px}.ss-ff-model-unavailable__actions{display:flex;gap:12px;flex-wrap:wrap;margin-top:8px}.ss-ff-model-unavailable__actions a,.ss-ff-contact-link{font-weight:700;color:#111;text-decoration:underline}
      .ss-ff-conflict{margin-bottom:12px;padding:12px;border:2px solid #a63f2d;border-radius:10px;background:#fff5f2;color:#581d14;font-weight:700}
      .ss-ff-divider{display:flex;align-items:center;gap:10px;font-size:12px;font-weight:700;opacity:.72;margin:14px 0}.ss-ff-divider:before,.ss-ff-divider:after{content:"";height:1px;background:rgba(0,0,0,.14);flex:1}
      .ss-ff-optional-equipment{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;align-items:end;margin-top:14px;padding:12px;border:1px solid rgba(0,0,0,.12);border-radius:10px;background:#f7f5f1}.ss-ff-optional-equipment label{grid-column:1/-1;font-weight:700;font-size:13px}
      .ss-ff-entry-card{display:flex;align-items:center;justify-content:space-between;gap:24px;margin:22px 0;padding:20px 22px;border:1px solid rgba(0,0,0,.14);border-radius:12px;background:#f7f5f1}
      .ss-ff-entry-card__copy h2{margin:0;font-size:clamp(20px,2.5vw,28px);line-height:1.2}.ss-ff-entry-card__copy p{margin:7px 0 0;max-width:680px;line-height:1.5}
      .ss-ff-entry-card__button{flex:0 0 auto;padding:12px 18px;border:1px solid #111;border-radius:8px;background:#111;color:#fff;font:inherit;font-weight:700;line-height:1.2;cursor:pointer}.ss-ff-entry-card__button:focus-visible{outline:3px solid #111;outline-offset:3px}
      .ss-ff-context-card{display:flex;align-items:center;justify-content:space-between;gap:20px;margin:0 0 22px;padding:18px 20px;border:1px solid rgba(0,0,0,.14);border-radius:12px;background:#f7f5f1}.ss-ff-context-card__copy h2{margin:0;font-size:20px;line-height:1.25}.ss-ff-context-card__copy p{margin:5px 0 0;line-height:1.45}.ss-ff-context-card__button{flex:0 0 auto;padding:11px 15px;border:1px solid #111;border-radius:8px;background:#111;color:#fff;font:inherit;font-weight:700;cursor:pointer}
      .ss-ff-product-entry{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 14px;border:1px solid rgba(0,0,0,.14);border-radius:10px;background:#f7f5f1}.ss-ff-product-entry span{font-weight:600}.ss-ff-product-entry__button{padding:9px 12px;border:1px solid #111;border-radius:8px;background:#fff;color:#111;font:inherit;font-weight:700;cursor:pointer;text-decoration:underline}.ss-ff-context-card button:focus-visible,.ss-ff-product-entry button:focus-visible{outline:3px solid #111;outline-offset:3px}
      @media(max-width:620px){.ss-ff-model-search__row,.ss-ff-optional-equipment{grid-template-columns:1fr}.ss-ff-model-option{align-items:flex-start;flex-direction:column}.ss-ff-model-option__action{white-space:normal}.ss-ff-entry-card,.ss-ff-context-card{align-items:stretch;flex-direction:column;gap:14px;padding:18px}.ss-ff-entry-card__button,.ss-ff-context-card__button{width:100%}.ss-ff-product-entry{align-items:stretch;flex-direction:column}.ss-ff-product-entry__button{width:100%}}
    `;
    document.head.appendChild(style);
  }

  function init() {
    const data = parseData();
    const panel = document.getElementById("ss-fit-finder-panel");
    if (!data || !panel) return;
    if (panel.dataset.fitFinderModelInitialized === "true") return;
    panel.dataset.fitFinderModelInitialized = "true";
    saveState({ allowed_product_handles: allProducts(data).map((product) => product.handle) });
    installStyles();
    addProminentSizingGuideEntry();
    addContextualEntries(data);
    addModelSearch(panel, data);

    const submit = panel.querySelector("[data-ff-submit]");
    const od = panel.querySelector("[data-ff-od]");
    const unit = panel.querySelector("[data-ff-unit]");
    if (submit && od && unit) {
      submit.addEventListener("click", () => {
        const measurement = currentMeasurement(data, od.value, unit.value);
        window.setTimeout(() => guardMeasurementResults(panel, data, measurement), 0);
      });
    }

    document.addEventListener("click", (event) => {
      const link = event.target.closest('[data-ff-results] a[href*="variant="]');
      if (!link) return;
      const id = new URL(link.href, window.location.origin).searchParams.get("variant");
      saveState({ product_click: true, selected_variant_id: id });
    });

    syncProductForms();
    const observer = new MutationObserver(syncProductForms);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
